// AgentReassignSheet — the canonical "pick a new owning / parent agent"
// modal for the admin UI.
//
// This is the single picker used by every flow that reassigns
// ownership inside the admin frontend:
//
//   • Player → new owning agent      (PlayersListPanel kebab + AdminUserDetail Ownership card)
//   • Player → detach to platform    (AdminUserDetail Ownership card)
//   • Agent  → new parent agent      (AgentsListPanel kebab)
//   • Agent  → detach to admin direct (AgentsListPanel kebab)
//
// Why a shared component:
//   - Before unification we had three different pickers (a select-
//     dropdown ReassignModal, a "rich card" OwnershipPicker, and one
//     mirror in agent-frontend). They drifted: cascade preview was
//     only in ReassignModal, the financial/hierarchy chips were only
//     in OwnershipPicker. Admins picked an agent without knowing the
//     wallet/deposit state of that agent.
//   - This component carries every signal an admin needs to decide
//     who to reassign to: identity (avatar + UAID + level + parent),
//     finance (pool wallet + deposit progress vs target), capacity
//     (direct players + direct sub-agents), and downline (separate
//     "in their sub-team" panel showing indirect counts).
//
// Two-step vs one-tap:
//   - When `preview` prop is set (agent → parent flow), picking a row
//     enters a confirm step that fetches `/admin/agents/:id/reassign-
//     preview` and shows cascade impact (level delta, descendants
//     affected, 7-level-cap violations) BEFORE the actual POST fires.
//     Reason input is collected on this confirm step.
//   - When `preview` is not set (player flows), picking a row fires
//     `onAssign` immediately — fast path the operator already
//     internalised, and the player-reassign is reversible/low-risk.
//
// What this component DOES NOT do:
//   - It does not call any reassign endpoint itself. The parent does
//     the network round-trip in `onAssign(newParentId, reason)`. This
//     keeps the sheet agnostic of /owner vs /reassign-agent vs
//     /reassign-parent and avoids accidentally coupling player and
//     agent flows.
//   - It does not auto-close. The parent calls `onClose()` after a
//     successful `onAssign` (or on cancel).
//
// Endpoint dependency:
//   The sheet fetches `/admin/hierarchy/agents?status=active&limit=200`
//   for the agent list. That endpoint returns the enriched row shape
//   (wallet_balance, deposit_balance, direct_players, direct_agents,
//   indirect_players, indirect_agents, level, uaid, parent_username).
//   See shared/src/core/controllers/adminHierarchy.controller.js.

import { useEffect, useMemo, useState } from 'react';
import { levelName } from '../lib/levelNames';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const SHEET_INPUT_CLS = `w-full rounded-xl px-3 py-2 text-[14px]
                         bg-white dark:bg-arena-800/60
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         text-neutral-900 dark:text-white
                         placeholder-arena-400 dark:placeholder-neon-200/40
                         focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40`;

// ─── Types ──────────────────────────────────────────────────────────

export interface AgentRow {
  id: number;
  uaid: string | null;
  username: string;
  email: string;
  avatar_url: string | null;
  status: 'active' | 'suspended';
  level: number;
  wallet_balance: number;
  security_deposit_target_points: number;
  deposit_balance: number;
  direct_players: number;
  direct_agents: number;
  indirect_players: number;
  indirect_agents: number;
  parent_username: string | null;
}

interface PreviewOk {
  ok: true;
  currentLevel: number;
  newLevel: number;
  levelDelta: number;
  affectedDescendants: number;
  totalAgentsAffected: number;
}
interface PreviewErr {
  ok: false;
  reason: string;
  newLevel?: number;
  deepestDescendantHops?: number;
  worstDescendantLevel?: number;
}
type PreviewState = PreviewOk | PreviewErr | null;

export interface AgentReassignSheetProps {
  /** Header context — what's being reassigned. */
  subject: {
    kind: 'agent' | 'player';
    /** Label shown in the header, e.g. '@username' (no leading @). */
    label: string;
    /** Currently owning agent id (or null = currently admin/platform direct). */
    currentParentId: number | null;
  };

  /** Agent ids to hide from the list — typically `[selfId, ...descendantIds]`
   *  for the agent→parent flow so the user can't pick themselves or a
   *  descendant (which would create a cycle). */
  excludeAgentIds?: number[];

  /** Optional detach card. Pass `null` to hide entirely. */
  detach?: {
    label: string;
    description: string;
    /** Material icon name shown in the avatar circle. */
    icon?: string;
  } | null;

  /** When set, enables the confirm step: clicking an agent row fetches
   *  `/admin/agents/{previewAgentId}/reassign-preview` and shows
   *  cascade impact before firing `onAssign`. Use for agent→parent. */
  preview?: {
    agentId: number;
  };

  /** Called when the user commits to an assignment. The parent is
   *  responsible for the actual POST and any post-assignment side
   *  effects. Throw to surface an error inline.
   *
   *  `pickedAgent` is the full row that was picked, or `null` when
   *  the user chose the detach option. Use it to render a toast with
   *  the new owner's @username without an extra lookup. */
  onAssign: (
    newParentId: number | null,
    reason: string | null,
    pickedAgent: AgentRow | null,
  ) => Promise<void>;

  onClose: () => void;
}

// ─── Component ──────────────────────────────────────────────────────

export default function AgentReassignSheet({
  subject, excludeAgentIds, detach = null, preview, onAssign, onClose,
}: AgentReassignSheetProps) {
  const [agents, setAgents]     = useState<AgentRow[] | null>(null);
  const [search, setSearch]     = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  /** Tracks the row currently in confirm-mode (preview required), or
   *  the literal 'detach' sentinel for the platform/admin-direct
   *  option. Null while browsing. */
  const [pendingPick, setPendingPick] = useState<AgentRow | 'detach' | null>(null);
  const [previewData, setPreviewData] = useState<PreviewState>(null);
  const [reason, setReason]           = useState('');

  // Load agent list. Status=active because picking a suspended agent
  // is universally a footgun (deposit guard, payment routing, etc.).
  useEffect(() => {
    let cancelled = false;
    adminApi.get<{ rows: AgentRow[] }>('/admin/hierarchy/agents?status=active&limit=200')
      .then((r) => { if (!cancelled) setAgents(r.rows); })
      .catch((e) => { if (!cancelled) setErr((e as ApiError).message); });
    return () => { cancelled = true; };
  }, []);

  // Auto-fetch cascade preview when a row is picked AND preview is
  // requested. Cancellation guard so rapid clicks don't race.
  useEffect(() => {
    if (!preview) { setPreviewData(null); return; }
    if (!pendingPick) { setPreviewData(null); return; }
    if (pendingPick === 'detach') {
      // Detach to admin-direct ⇒ new level 1, no descendants moved
      // server-side beyond cascade. We still call the preview endpoint
      // for consistency so the impact panel renders the same way.
      let cancelled = false;
      setPreviewData(null);
      adminApi.get<PreviewOk | PreviewErr>(
        `/admin/agents/${preview.agentId}/reassign-preview?new_parent_agent_id=`
      ).then((r) => { if (!cancelled) setPreviewData(r); })
       .catch((e) => { if (!cancelled) setErr((e as ApiError).message); });
      return () => { cancelled = true; };
    }
    let cancelled = false;
    setPreviewData(null);
    adminApi.get<PreviewOk | PreviewErr>(
      `/admin/agents/${preview.agentId}/reassign-preview?new_parent_agent_id=${pendingPick.id}`
    ).then((r) => { if (!cancelled) setPreviewData(r); })
     .catch((e) => { if (!cancelled) setErr((e as ApiError).message); });
    return () => { cancelled = true; };
  }, [preview, pendingPick]);

  // Filter and sort. Excluded ids are dropped silently — admin already
  // knows they can't pick those. Search matches username/uaid/email.
  const visibleAgents = useMemo(() => {
    if (!agents) return null;
    const ex = new Set((excludeAgentIds || []).map(Number));
    const q = search.trim().toLowerCase();
    return agents.filter((a) => {
      if (ex.has(Number(a.id))) return false;
      if (!q) return true;
      return a.username.toLowerCase().includes(q)
          || a.email.toLowerCase().includes(q)
          || (a.uaid || '').toLowerCase().includes(q);
    });
  }, [agents, excludeAgentIds, search]);

  // The detach card is the equivalent of picking "no parent". When
  // preview is enabled we show the confirm flow for it too; otherwise
  // we fire onAssign right away.
  async function handleRowClick(row: AgentRow) {
    if (preview) { setPendingPick(row); setErr(null); return; }
    setBusy(true); setErr(null);
    try { await onAssign(row.id, null, row); }
    catch (e) { setErr((e as ApiError).message); setBusy(false); }
  }
  async function handleDetachClick() {
    if (preview) { setPendingPick('detach'); setErr(null); return; }
    setBusy(true); setErr(null);
    try { await onAssign(null, null, null); }
    catch (e) { setErr((e as ApiError).message); setBusy(false); }
  }
  async function confirmPending() {
    if (!pendingPick) return;
    setBusy(true); setErr(null);
    try {
      const id   = pendingPick === 'detach' ? null : pendingPick.id;
      const row  = pendingPick === 'detach' ? null : pendingPick;
      await onAssign(id, reason.trim() || null, row);
    } catch (e) { setErr((e as ApiError).message); setBusy(false); }
  }
  function cancelPending() { setPendingPick(null); setPreviewData(null); setReason(''); setErr(null); }

  return (
    <div className="fixed inset-0 z-[80] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
         role="dialog" aria-modal="true">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 360, damping: 28 }}
        className="w-full max-w-md rounded-2xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-2xl overflow-hidden flex flex-col my-4"
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b ring-arena-200/50 dark:ring-arena-600/40 shrink-0">
          <Icon name={subject.kind === 'agent' ? 'account_tree' : 'support_agent'}
                size={18} className="text-emerald-700 dark:text-emerald-300" />
          <h2 className="text-[15px] font-bold text-neutral-900 dark:text-white flex-1">
            Reassign @{subject.label}
          </h2>
          <button onClick={onClose} disabled={busy}
                  className="grid place-items-center w-8 h-8 rounded-full
                             hover:bg-arena-100/60 dark:hover:bg-arena-700/70
                             text-arena-600 dark:text-neon-200/70 disabled:opacity-40"
                  aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        {pendingPick ? (
          <ConfirmPane
            subject={subject}
            pick={pendingPick}
            previewData={previewData}
            reason={reason}
            setReason={setReason}
            err={err}
            busy={busy}
            onConfirm={confirmPending}
            onBack={cancelPending}
          />
        ) : (
          <PickerPane
            subject={subject}
            detach={detach}
            search={search}
            setSearch={setSearch}
            visibleAgents={visibleAgents}
            err={err}
            busy={busy}
            onRowClick={handleRowClick}
            onDetachClick={handleDetachClick}
          />
        )}
      </motion.div>
    </div>
  );
}

// ─── Picker pane (browse-and-pick) ──────────────────────────────────

function PickerPane({
  subject, detach, search, setSearch, visibleAgents, err, busy,
  onRowClick, onDetachClick,
}: {
  subject: AgentReassignSheetProps['subject'];
  detach: AgentReassignSheetProps['detach'];
  search: string;
  setSearch: (v: string) => void;
  visibleAgents: AgentRow[] | null;
  err: string | null;
  busy: boolean;
  onRowClick: (a: AgentRow) => void;
  onDetachClick: () => void;
}) {
  return (
    <div className="p-3 space-y-2.5 overflow-y-auto">
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 leading-relaxed">
        {subject.kind === 'agent'
          ? 'Pick a new parent agent — or detach to admin-direct. The reassignment cascades through every descendant; the impact panel previews it before commit.'
          : 'Pick a new agent — or detach to platform-direct (no agent owns this player). Future commissions follow the new owner; already-credited rows aren\'t touched.'}
      </p>

      {detach && (
        <button
          onClick={onDetachClick}
          disabled={busy || subject.currentParentId == null}
          className="w-full flex items-center gap-3 p-3 rounded-xl
                     bg-violet-50 hover:bg-violet-100 ring-1 ring-inset ring-violet-200/60
                     dark:bg-violet-400/10 dark:hover:bg-violet-400/20 dark:ring-violet-400/30
                     disabled:opacity-40 disabled:cursor-not-allowed text-left transition"
        >
          <div className="grid place-items-center w-9 h-9 rounded-full
                          bg-violet-100 text-violet-700
                          dark:bg-violet-500/20 dark:text-violet-300 shrink-0">
            <Icon name={detach.icon || 'shield'} size={16} filled />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white">{detach.label}</div>
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70">{detach.description}</div>
          </div>
          {subject.currentParentId == null && (
            <span className="text-[10px] font-semibold uppercase text-arena-600 dark:text-neon-200/70">current</span>
          )}
        </button>
      )}

      <div className="relative">
        <Icon name="search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-500 dark:text-neon-200/45" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agents — name, UAID, email…"
          className={SHEET_INPUT_CLS + ' pl-8'}
        />
      </div>

      <div className="space-y-1.5">
        {visibleAgents === null && (
          <div className="text-center py-6 text-[12px] text-arena-600 dark:text-neon-200/70">
            <Icon name="progress_activity" size={16} className="animate-spin inline-block" /> Loading agents…
          </div>
        )}
        {visibleAgents && visibleAgents.length === 0 && (
          <div className="text-center py-6 text-[12px] text-arena-600 dark:text-neon-200/70">
            {search ? 'No agents match your search.' : 'No agents available to pick.'}
          </div>
        )}
        {visibleAgents && visibleAgents.map((a) => (
          <AgentRowCard
            key={a.id}
            agent={a}
            isCurrent={Number(subject.currentParentId) === Number(a.id)}
            busy={busy}
            onClick={() => onRowClick(a)}
          />
        ))}
      </div>

      {err && (
        <div className="px-3 py-2 rounded-lg ring-1 ring-inset ring-red-300/60 dark:ring-red-800/60
                        bg-rose-50 dark:bg-rose-500/10 text-red-700 dark:text-red-200 text-[11.5px]">
          {err}
        </div>
      )}
    </div>
  );
}

// ─── Confirm pane (two-step: preview + reason) ──────────────────────

function ConfirmPane({
  subject, pick, previewData, reason, setReason, err, busy,
  onConfirm, onBack,
}: {
  subject: AgentReassignSheetProps['subject'];
  pick: AgentRow | 'detach';
  previewData: PreviewState;
  reason: string;
  setReason: (v: string) => void;
  err: string | null;
  busy: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const isDetach = pick === 'detach';
  const blocked = previewData && previewData.ok === false;
  const ready   = previewData && previewData.ok === true && !busy;

  return (
    <div className="p-3 space-y-3 overflow-y-auto">
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60 flex items-center gap-1">
        <Icon name="check_circle" size={12} className="text-emerald-700 dark:text-emerald-300" />
        Confirm reassignment
      </div>

      {/* Selected target card */}
      {isDetach ? (
        <div className="flex items-center gap-3 p-3 rounded-xl
                        bg-violet-50 ring-1 ring-inset ring-violet-200/60
                        dark:bg-violet-400/10 dark:ring-violet-400/30">
          <div className="grid place-items-center w-10 h-10 rounded-full
                          bg-violet-100 text-violet-700
                          dark:bg-violet-500/20 dark:text-violet-300 shrink-0">
            <Icon name="shield" size={18} filled />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white">
              {subject.kind === 'agent' ? 'Detach to admin direct' : 'Detach to platform direct'}
            </div>
            <div className="text-[11px] text-arena-600 dark:text-neon-200/70">
              {subject.kind === 'agent' ? 'Removes parent agent. Agent reports directly to admin.' : 'Removes agent ownership. Admin manages directly.'}
            </div>
          </div>
        </div>
      ) : (
        <AgentRowCard agent={pick} isCurrent={false} busy={false} compact />
      )}

      {/* Cascade preview — when present */}
      {subject.kind === 'agent' && (
        <div className={`rounded-xl ring-1 ring-inset p-3 text-[12px] ${
          previewData === null
            ? 'bg-arena-50 dark:bg-arena-700/30 ring-arena-200/60 dark:ring-arena-600/40'
          : previewData.ok
            ? 'bg-cyan-50 dark:bg-cyan-950/30 ring-cyan-300/60 dark:ring-cyan-800/60 text-cyan-900 dark:text-cyan-200'
            : 'bg-rose-50 dark:bg-rose-500/10 ring-red-300/60 dark:ring-red-800/60 text-red-900 dark:text-red-200'
        }`}>
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Icon name={previewData?.ok === false ? 'block' : 'change_history'} size={12} />
            {previewData === null ? 'Computing impact…' : previewData.ok ? 'Cascade impact' : 'Reassignment blocked'}
          </div>
          {previewData === null && (
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60">
              <Icon name="progress_activity" size={12} className="animate-spin inline-block" /> Fetching preview…
            </div>
          )}
          {previewData?.ok && (
            <ul className="space-y-0.5">
              <li>Level: <b>{levelName(previewData.currentLevel)} → {levelName(previewData.newLevel)}</b> ({previewData.levelDelta >= 0 ? '+' : ''}{previewData.levelDelta})</li>
              <li>Affected descendants: <b>{previewData.affectedDescendants}</b> agent{previewData.affectedDescendants === 1 ? '' : 's'}</li>
              <li>Total agents touched (incl. this one): <b>{previewData.totalAgentsAffected}</b></li>
            </ul>
          )}
          {previewData && previewData.ok === false && (
            <div>{explainPreviewError(previewData)}</div>
          )}
        </div>
      )}

      {/* Reason input — always available for the confirm step */}
      <label className="block">
        <div className="text-[10px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60 mb-1">
          Reason (optional)
        </div>
        <input
          type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. agent left, transferred to new region"
          className={SHEET_INPUT_CLS} maxLength={190}
        />
      </label>

      {err && (
        <div className="px-3 py-2 rounded-lg ring-1 ring-inset ring-red-300/60 dark:ring-red-800/60
                        bg-rose-50 dark:bg-rose-500/10 text-red-700 dark:text-red-200 text-[11.5px]">
          {err}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onBack} disabled={busy}
                className="px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wider
                           text-arena-600 dark:text-neon-200/70 hover:text-arena-900 dark:hover:text-white disabled:opacity-40">
          ← Pick different
        </button>
        <button onClick={onConfirm}
                disabled={busy || !!blocked || (subject.kind === 'agent' && !ready)}
                className="px-4 py-2 rounded-lg text-[12px] font-bold uppercase tracking-wider
                           bg-emerald-600 text-white hover:bg-emerald-500
                           disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? 'Saving…' : blocked ? 'Blocked' : 'Confirm reassign'}
        </button>
      </div>
    </div>
  );
}

// ─── Reusable row card (used by picker + confirm pane) ──────────────

export function AgentRowCard({
  agent: a, isCurrent, busy, compact, onClick,
}: {
  agent: AgentRow;
  isCurrent: boolean;
  busy: boolean;
  compact?: boolean;
  onClick?: () => void;
}) {
  const depTarget = Number(a.security_deposit_target_points || 0);
  const depBal    = Number(a.deposit_balance || 0);
  const depPct    = depTarget > 0 ? Math.min(100, Math.round((depBal / depTarget) * 100)) : null;
  const depShort  = depTarget > 0 && depBal < depTarget;
  const indirectPlayers = Number(a.indirect_players || 0);
  const indirectAgents  = Number(a.indirect_agents || 0);
  const hasSubTeam      = indirectPlayers > 0 || indirectAgents > 0;

  const inner = (
    <>
      {/* Identity row */}
      <div className="flex items-center gap-2.5 min-w-0">
        <AdminAvatar url={a.avatar_url} username={a.username} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">@{a.username}</span>
            <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold leading-none
                             bg-violet-100 text-violet-700
                             dark:bg-violet-500/15 dark:text-violet-300">
              {levelName(a.level || 1)}
            </span>
            {a.parent_username && (
              <span className="hidden sm:inline shrink-0 text-[9.5px] text-arena-500 dark:text-neon-200/55 truncate">
                ← @{a.parent_username}
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
            {a.uaid ? `${a.uaid} · ` : ''}{a.email}
          </div>
        </div>
        {busy && <Icon name="progress_activity" size={14} className="animate-spin text-arena-500 dark:text-neon-200/45" />}
        {isCurrent && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase
                           bg-emerald-100 text-emerald-700
                           dark:bg-emerald-500/15 dark:text-emerald-300">
            Current
          </span>
        )}
      </div>

      {/* Direct ownership chips */}
      <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
        <StatChip
          icon="account_balance_wallet" label="Pool" value={inr(a.wallet_balance || 0)}
          tone={Number(a.wallet_balance) >= 0 ? 'emerald' : 'red'}
        />
        <StatChip
          icon="savings" label="Deposit"
          value={depTarget > 0 ? `${depPct}%` : inr(depBal)}
          sub={depTarget > 0 ? inr(depBal) : null}
          tone={depTarget === 0 ? 'slate' : depShort ? 'amber' : 'emerald'}
        />
        <StatChip
          icon="group" label="Players" value={String(a.direct_players || 0)} tone="cyan"
        />
        <StatChip
          icon="account_tree" label="Sub-agents" value={String(a.direct_agents || 0)} tone="violet"
        />
      </div>

      {/* Indirect / sub-team panel — only when downline exists */}
      {hasSubTeam && (
        <div className="rounded-lg px-2.5 py-1.5
                        bg-arena-100/70 ring-1 ring-inset ring-arena-200/50
                        dark:bg-arena-900/30 dark:ring-arena-600/30">
          <div className="flex items-center gap-1 mb-1">
            <Icon name="subdirectory_arrow_right" size={11} className="text-arena-500 dark:text-neon-200/60" />
            <span className="text-[8.5px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60">
              In their sub-team
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Icon name="group" size={12} className="text-cyan-700 dark:text-cyan-300 shrink-0" />
              <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate">Players</span>
              <span className="ml-auto text-[12px] font-bold text-cyan-700 dark:text-cyan-300">{indirectPlayers}</span>
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              <Icon name="account_tree" size={12} className="text-violet-700 dark:text-violet-300 shrink-0" />
              <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate">Sub-agents</span>
              <span className="ml-auto text-[12px] font-bold text-violet-700 dark:text-violet-300">{indirectAgents}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const baseCls = 'w-full flex flex-col gap-2 p-2.5 rounded-xl text-left transition';
  const interactiveCls = `
    bg-arena-50/60 hover:bg-arena-100/60 ring-1 ring-inset ring-arena-200/60
    dark:bg-arena-700/35 dark:hover:bg-arena-700/60 dark:ring-arena-600/40
    hover:ring-emerald-300 dark:hover:ring-emerald-400/40
    disabled:opacity-50 disabled:cursor-not-allowed`;
  const staticCls = `
    bg-emerald-50/60 ring-1 ring-inset ring-emerald-300/60
    dark:bg-emerald-500/10 dark:ring-emerald-400/30`;

  if (compact || !onClick) {
    return <div className={`${baseCls} ${staticCls}`}>{inner}</div>;
  }
  return (
    <button onClick={onClick} disabled={busy || isCurrent}
            className={`${baseCls} ${interactiveCls}`}>
      {inner}
    </button>
  );
}

// ─── Stat chip primitive ────────────────────────────────────────────

type ChipTone = 'emerald' | 'cyan' | 'violet' | 'amber' | 'red' | 'slate';
const CHIP_TONES: Record<ChipTone, string> = {
  emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/25',
  cyan:    'bg-cyan-50 text-cyan-800 ring-cyan-200/60 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-400/25',
  violet:  'bg-violet-50 text-violet-800 ring-violet-200/60 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/25',
  amber:   'bg-amber-50 text-amber-800 ring-amber-200/60 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/25',
  red:     'bg-red-50 text-red-700 ring-red-200/60 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-400/25',
  slate:   'bg-arena-100 text-arena-700 ring-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/70 dark:ring-arena-600/40',
};

function StatChip({
  icon, label, value, sub, tone,
}: {
  icon: string; label: string; value: string; sub?: string | null; tone: ChipTone;
}) {
  return (
    <div className={`flex flex-col items-center justify-center gap-0.5 px-1.5 py-1.5 rounded-lg ring-1 ring-inset ${CHIP_TONES[tone]}`}>
      <div className="flex items-center gap-1 leading-none">
        <Icon name={icon} size={10} className="opacity-80" />
        <span className="text-[8.5px] font-bold uppercase tracking-wide opacity-75 truncate">{label}</span>
      </div>
      <div className="text-[11px] font-bold leading-none truncate w-full text-center">{value}</div>
      {sub && <div className="text-[8.5px] opacity-70 leading-none truncate w-full text-center">{sub}</div>}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

/** Translates the controller's machine-readable preview error reasons
 *  into a sentence the admin can act on. Kept in sync with the
 *  `ok: false` branches in shared/src/core/controllers/adminHierarchy
 *  .controller.js → reassignPreview. */
function explainPreviewError(pe: PreviewErr): string {
  switch (pe.reason) {
    case 'depth_cap_7_exceeded':
      return `Would put agent at level ${pe.newLevel} (exceeds 7-level cap).`;
    case 'descendant_depth_cap_7_exceeded':
      return `Would push deepest descendant to level ${pe.worstDescendantLevel ?? '?'} (exceeds 7-level cap). The deepest descendant is ${pe.deepestDescendantHops ?? '?'} hop(s) below this agent.`;
    case 'circular':
      return 'Target is a descendant of this agent (would create a cycle).';
    case 'self_parent':
      return 'Cannot make agent its own parent.';
    case 'new_parent_not_found':
      return 'New parent agent not found.';
    default:
      return pe.reason;
  }
}
